import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { formatDuration, formatTimeAgo, getFileIcon } from '../utils/helpers';
import toast from 'react-hot-toast';
import { Plus, ListVideo, Pencil, Trash2, X, GripVertical, Clock, Monitor } from 'lucide-react';

export default function PlaylistsPage() {
  const [playlists, setPlaylists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editPlaylist, setEditPlaylist] = useState(null);
  const [detailPlaylist, setDetailPlaylist] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [mediaFiles, setMediaFiles] = useState([]);
  const [showAddMedia, setShowAddMedia] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');

  const fetchPlaylists = async () => {
    try {
      const { data } = await api.get('/playlists');
      setPlaylists(data.playlists || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const fetchDetail = async (id) => {
    try {
      const { data } = await api.get(`/playlists/${id}`);
      setDetailData(data);
    } catch (err) { toast.error('Failed to load playlist'); }
  };

  const fetchMedia = async () => {
    try {
      const { data } = await api.get('/media');
      setMediaFiles(data.files || []);
    } catch (err) { console.error(err); }
  };

  useEffect(() => { fetchPlaylists(); fetchMedia(); }, []);
  useEffect(() => { if (detailPlaylist) fetchDetail(detailPlaylist); }, [detailPlaylist]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await api.post('/playlists', { name: newName, description: newDesc });
      toast.success('Playlist created');
      setShowCreate(false); setNewName(''); setNewDesc('');
      fetchPlaylists();
    } catch (err) { toast.error('Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this playlist?')) return;
    try {
      await api.delete(`/playlists/${id}`);
      toast.success('Deleted');
      if (detailPlaylist === id) { setDetailPlaylist(null); setDetailData(null); }
      fetchPlaylists();
    } catch (err) { toast.error('Failed'); }
  };

  const handleAddItem = async (mediaId) => {
    try {
      await api.post(`/playlists/${detailPlaylist}/items`, {
        media_id: mediaId,
        duration_seconds: 10,
      });
      toast.success('Item added');
      fetchDetail(detailPlaylist);
      fetchPlaylists();
    } catch (err) { toast.error('Failed'); }
  };

  const handleRemoveItem = async (itemId) => {
    try {
      await api.delete(`/playlists/${detailPlaylist}/items/${itemId}`);
      toast.success('Item removed');
      fetchDetail(detailPlaylist);
      fetchPlaylists();
    } catch (err) { toast.error('Failed'); }
  };

  const handleDurationChange = async (itemId, seconds) => {
    try {
      await api.put(`/playlists/${detailPlaylist}/items/${itemId}`, {
        duration_seconds: parseInt(seconds) || 10,
      });
      fetchDetail(detailPlaylist);
    } catch (err) { console.error(err); }
  };

  if (loading) return <div className="loading-page"><span className="spinner" /></div>;

  return (
    <div className="page-content">
      <div className="page-header">
        <div>
          <h2>Playlists</h2>
          <div className="page-header-sub">{playlists.length} playlists</div>
        </div>
        <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
          <Plus size={16} /> New Playlist
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: detailPlaylist ? '1fr 1fr' : '1fr', gap: 20 }}>
        {/* Playlist List */}
        <div>
          {playlists.length === 0 ? (
            <div className="card">
              <div className="empty-state">
                <ListVideo size={48} />
                <h3>No playlists yet</h3>
                <p>Create your first playlist and add media files to it.</p>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {playlists.map(p => (
                <div key={p.id}
                  className="card card-padded"
                  style={{
                    cursor: 'pointer',
                    borderColor: detailPlaylist === p.id ? 'var(--accent)' : undefined,
                  }}
                  onClick={() => setDetailPlaylist(p.id)}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 15 }}>{p.name}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 4, display: 'flex', gap: 12 }}>
                        <span>{p.item_count || 0} items</span>
                        <span><Clock size={11} style={{ verticalAlign: -1 }} /> {formatDuration(p.total_duration)}</span>
                        <span><Monitor size={11} style={{ verticalAlign: -1 }} /> {p.assigned_devices || 0} TVs</span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 4 }} onClick={e => e.stopPropagation()}>
                      <button className="btn btn-ghost btn-icon btn-sm"
                        style={{ color: 'var(--danger)' }}
                        onClick={() => handleDelete(p.id)}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Playlist Detail */}
        {detailPlaylist && detailData && (
          <div className="card card-padded">
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
              <div>
                <h3 style={{ fontSize: 17, fontWeight: 600 }}>{detailData.playlist?.name}</h3>
                {detailData.playlist?.description && (
                  <p style={{ fontSize: 13, color: 'var(--text-tertiary)', marginTop: 4 }}>
                    {detailData.playlist.description}
                  </p>
                )}
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button className="btn btn-primary btn-sm" onClick={() => { setShowAddMedia(true); }}>
                  <Plus size={14} /> Add Media
                </button>
                <button className="btn btn-ghost btn-icon btn-sm" onClick={() => { setDetailPlaylist(null); setDetailData(null); }}>
                  <X size={16} />
                </button>
              </div>
            </div>

            {/* Assigned Devices */}
            {detailData.assigned_devices?.length > 0 && (
              <div style={{ marginBottom: 16, padding: '8px 12px', background: 'var(--bg-tertiary)', borderRadius: 'var(--radius-md)', fontSize: 12 }}>
                <span style={{ color: 'var(--text-tertiary)' }}>Assigned to: </span>
                {detailData.assigned_devices.map(d => (
                  <span key={d.id} className="badge badge-info" style={{ marginLeft: 4 }}>
                    {d.tv_number || d.name}
                  </span>
                ))}
              </div>
            )}

            {/* Playlist Items */}
            {detailData.items?.length === 0 ? (
              <div className="empty-state" style={{ padding: 30 }}>
                <p>No items in this playlist. Click "Add Media" to start.</p>
              </div>
            ) : (
              <div>
                {detailData.items.map((item, idx) => (
                  <div key={item.id} className="playlist-item">
                    <div className="playlist-item-drag"><GripVertical size={16} /></div>
                    <div className="playlist-item-thumb">
                      <span style={{ fontSize: 18 }}>{getFileIcon(item.file_type)}</span>
                    </div>
                    <div className="playlist-item-info">
                      <div className="playlist-item-name">{item.original_name}</div>
                      <div className="playlist-item-meta">{item.file_type}</div>
                    </div>
                    <div className="playlist-item-duration">
                      <input className="form-input" type="number" min="1" max="3600"
                        style={{ padding: '4px 8px', fontSize: 13, textAlign: 'center' }}
                        defaultValue={item.duration_seconds}
                        onBlur={e => handleDurationChange(item.id, e.target.value)}
                      />
                      <div style={{ fontSize: 10, textAlign: 'center', color: 'var(--text-tertiary)', marginTop: 2 }}>seconds</div>
                    </div>
                    <div className="playlist-item-actions">
                      <button className="btn btn-ghost btn-icon btn-sm"
                        style={{ color: 'var(--danger)' }}
                        onClick={() => handleRemoveItem(item.id)}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreate && (
        <div className="modal-overlay" onClick={() => setShowCreate(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Create Playlist</h3>
              <button className="btn btn-ghost btn-icon" onClick={() => setShowCreate(false)}><X size={18} /></button>
            </div>
            <form onSubmit={handleCreate}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Name</label>
                  <input className="form-input" value={newName}
                    onChange={e => setNewName(e.target.value)}
                    placeholder="Morning Classes" required autoFocus />
                </div>
                <div className="form-group">
                  <label className="form-label">Description (optional)</label>
                  <textarea className="form-input" value={newDesc}
                    onChange={e => setNewDesc(e.target.value)}
                    placeholder="Content for morning sessions" />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowCreate(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary">Create</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Media Modal */}
      {showAddMedia && (
        <div className="modal-overlay" onClick={() => setShowAddMedia(false)}>
          <div className="modal" style={{ maxWidth: 600 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add Media to Playlist</h3>
              <button className="btn btn-ghost btn-icon" onClick={() => setShowAddMedia(false)}><X size={18} /></button>
            </div>
            <div className="modal-body" style={{ maxHeight: 400, overflowY: 'auto' }}>
              {mediaFiles.length === 0 ? (
                <p style={{ color: 'var(--text-tertiary)' }}>No media files. Upload some first.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {mediaFiles.map(f => {
                    const alreadyAdded = detailData?.items?.some(i => i.media_uuid === f.uuid);
                    return (
                      <button key={f.id}
                        className="btn btn-secondary"
                        style={{
                          justifyContent: 'flex-start',
                          opacity: alreadyAdded ? 0.5 : 1,
                        }}
                        disabled={alreadyAdded}
                        onClick={() => { handleAddItem(f.id); }}>
                        <span style={{ fontSize: 16 }}>{getFileIcon(f.file_type)}</span>
                        <span style={{ flex: 1, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {f.original_name}
                        </span>
                        {alreadyAdded && <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>Added</span>}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
